<script setup>
import { computed, watch, ref, onMounted } from 'vue'

import { useStore } from 'vuex'
import FormButton from '../Atoms/FormButton.vue';
import IconBase from '../icon/IconBase.vue';
import IconPlus from '../icon/IconPlus.vue';
import IconBookOpen from '../icon/IconBookOpen.vue';
import IconAlertTriangleSolid from '../icon/IconAlertTriangleSolid.vue';
import FormSwitch from '../Atoms/FormSwitch.vue';

const store = useStore()

const paywallActive = ref(false)

const userPaywallActived = computed(() => store.getters['User/userPaywallActived'])
const userPaywall = computed(() => store.getters['User/userPaywall'])
const hasLiveSubscription = computed(() => store.getters['User/hasLiveSubscription'])
const paywalls = computed(() => store.getters['Paywalls/paywalls'])

const props = defineProps({
  onStripeConnectClick: {
    type: Function,
  },
  onStripeConnectCancelClick: {
    type: Function
  },
  stripeConnectProcessing: {
    type: Boolean,
  },
  stripeOauthLink: {
    type: String
  },
  stripeConnectLinkError: {
    type: Boolean
  },
  paywallActive: {
    type: Boolean
  },
  isStripePending: {
    type: Boolean,
    default: false,
  },
  stripeStatus: {
    type: Object,
    default: null,
  }

})

// The parent decides what unlink / switch need first (open paywalls, sites
// billed through this account), as it does for the grid's Stripe controls.
const emit = defineEmits(['unlink-stripe', 'switch-stripe'])

const showStripeConnect = computed(() => !userPaywallActived.value || props.isStripePending)

// Human-friendly labels for the most common Stripe onboarding requirements.
const REQ_LABELS = {
  'external_account': 'Bank account or debit card',
  'individual.verification.document': 'Identity document',
  'individual.verification.additional_document': 'Additional identity document',
  'individual.verification.proof_of_liveness': 'Identity verification (selfie)',
  'individual.id_number': 'ID or SSN',
  'individual.ssn_last_4': 'Last 4 digits of SSN',
  'individual.dob.day': 'Date of birth',
  'individual.address.line1': 'Home address',
  'individual.phone': 'Phone number',
  'business_profile.url': 'Business website',
  'business_profile.mcc': 'Business category',
  'business_profile.product_description': 'Product description',
  'business_profile.support_phone': 'Support phone number',
  'support_phone': 'Support phone number',
  'company.tax_id': 'Company tax ID',
  'company.verification.document': 'Company document',
  'company.phone': 'Company phone number',
  'tos_acceptance.date': "Accept Stripe's terms of service",
  'tos_acceptance.ip': "Accept Stripe's terms of service",
  'tos_acceptance.user_agent': "Accept Stripe's terms of service",
}

// Fallback labels matched by the last key segment (regardless of prefix).
const TAIL_LABELS = {
  'proof_of_liveness': 'Identity verification (selfie)',
  'external_account': 'Bank account or debit card',
  'document': 'Identity document',
}

// Acronyms that should stay uppercase in the humanized fallback.
const ACRONYMS = { ip: 'IP', ssn: 'SSN', id: 'ID', url: 'URL', vat: 'VAT', ein: 'EIN', mcc: 'MCC' }

const humanizeReq = (key) => {
  if (REQ_LABELS[key]) return REQ_LABELS[key]
  const tail = String(key).split('.').pop()
  if (TAIL_LABELS[tail]) return TAIL_LABELS[tail]
  if (ACRONYMS[tail.toLowerCase()]) return ACRONYMS[tail.toLowerCase()]
  const words = tail.replace(/_/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

const outstandingItems = computed(() => {
  const s = props.stripeStatus
  if (!s) return []
  const keys = [...new Set([...(s.past_due || []), ...(s.currently_due || [])])]
  // Dedupe labels (e.g. several tos_acceptance.* keys map to one line).
  return [...new Set(keys.map(humanizeReq))]
})

const stripeNeedsAction = computed(() => !!props.stripeStatus?.needs_action)
const stripeUnderReview = computed(() => !!props.stripeStatus?.under_review)

const connectLabel = computed(() =>
  props.isStripePending ? 'Continue Stripe verification' : 'Connect to Stripe'
)

</script>

<template>
  <main v-if="showStripeConnect">
    <section class="md:w-1/2 mx-auto">
      <img class="mx-auto mb-3" height="80" width="80" src="@/assets/icons/secure.svg"  />
      <h2 class="text-center text-xl font-medium">Monetize your content with Paywall</h2>
      <p class="text-center text-surface-6 text-sm mb-4">Paywall protects your livestreams and on-demand videos so only
        paying viewers can access them.
        <a class="underline" target="_blank" href="https://docs.castr.com/en/collections/2958860-castr-paywall">Learn
          more</a>
      </p>
    </section>
    <section class="mt-10 md:w-1/2 mx-auto bg-surface-2 rounded-xl p-8">
      <ol class="mb-0 p-0">
        <li class="custom-list-item before:content-['1'] text-sm">
          <div class="flex flex-col bordered items-start">
            <img width="86" class="mb-3" src="@/assets/images/stripe-logo.svg"  />
            <p class="mb-2">Set up Stripe to receive payments.</p>

            <p class="text-surface-6 mb-4">
              Connect your Stripe account to start using Castr Paywall. Payments from your viewers are deposited
              directly into your Stripe account.
            </p>
            <FormButton v-if="!stripeConnectProcessing" @click="onStripeConnectClick">{{ connectLabel }}</FormButton>
            <div v-if="stripeOauthLink" class="flex">
              <p v-if="stripeConnectProcessing" class="text-center text-white mb-1">
                Connecting to Stripe...
                <i class="fas fa-spinner fa-spin" />
                <FormButton @click="onStripeConnectCancelClick">
                  Cancel
                </FormButton>
              </p>
              <p v-if="stripeConnectLinkError" class="text-center text-danger mb-2 mt-2">
                Couldn't create the Stripe link. Please try again.
              </p>
            </div>
            <div
              v-if="isStripePending"
              class="mt-3 w-full flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3"
            >
              <icon-base class="shrink-0 mt-0.5 text-warning" :width="16" :height="16" fill="none">
                <icon-alert-triangle-solid />
              </icon-base>
              <div class="text-sm text-surface-9 m-0">
                <!-- Stripe still needs details from the merchant -->
                <template v-if="stripeNeedsAction">
                  <p class="m-0 mb-1 font-medium text-surface-text-1">Stripe needs a few more details to finish verifying your account.</p>
                  <ul v-if="outstandingItems.length" class="list-disc pl-5 mb-2">
                    <li v-for="(item, i) in outstandingItems" :key="i">{{ item }}</li>
                  </ul>
                  <p class="m-0">Click <span class="font-medium">“Continue Stripe verification”</span> above to finish.</p>
                </template>
                <!-- Submitted, waiting on Stripe review -->
                <template v-else-if="stripeUnderReview">
                  <p class="m-0">Your details are submitted and <span class="font-medium">under review by Stripe</span>. No action needed — this usually completes within a few minutes, and can take up to a couple of days for some accounts.</p>
                </template>
                <!-- Fallback / unknown -->
                <template v-else>
                  Your Stripe account is currently pending verification. This usually takes a few minutes to complete.
                  If the status is still pending after some time, click <span class="font-medium">“Continue Stripe verification”</span> above, or
                  <a class="underline" target="_blank" href="https://dashboard.stripe.com/login">log in to your Stripe account</a>
                  to complete any remaining steps.
                </template>
              </div>
            </div>
            <button
              v-if="userPaywall"
              type="button"
              @click="emit('switch-stripe')"
              class="mt-3 text-sm text-surface-7 hover:text-surface-9 underline underline-offset-2"
            >
              Use a different Stripe account
            </button>
            <hr class="w-full my-3">
          </div>
        </li>
        <li class="custom-list-item before:content-['2'] text-sm">
          <div class="w-full !mt-1">
            Choose the livestream or on-demand video you want to protect.
            <hr class="w-full my-3">
          </div>
        </li>
        <li class="custom-list-item before:content-['3'] text-sm">
          <div class="w-full !mt-1">
            Your audience pays to unlock access to your content.
            <hr class="w-full my-3">
          </div>
        </li>
        <li class="custom-list-item before:content-['4'] text-sm">
          <div class="!mt-1">
            You get paid directly through Stripe.
          </div>
        </li>
      </ol>
    </section>
  </main>

  <main v-else-if="paywalls.length === 0 && !isStripePending">
    <section class="flex flex-col items-center mt-32">
      <h2 class="text-sm text-surface-9 font-medium mb-2">Paywall creation simplified</h2>
      <p class="text-sm text-surface-8 mb-4">Design your own paywall and share it with
        your audience</p>
      <div class="flex flex-col gap-y-3 mx-auto w-[320px]">
        <FormButton class="text-center justify-center" type="secondary" href="https://docs.castr.com/en/articles/8925810-how-to-create-a-paywall"
          target="_blank">
          <icon-base class="mr-1">
            <icon-book-open />
          </icon-base>
          <span class="ml-2">How Paywall works</span>
        </FormButton>
        <FormButton class="text-center justify-center" v-if="hasLiveSubscription" @click="$showModal('add-paywall-modal')">
          <icon-base class="mr-1">
            <icon-plus />
          </icon-base>
          Create Paywall
        </FormButton>
        <FormButton class="text-center justify-center" v-else to="/subscribe">
          Upgrade
        </FormButton>
      </div>

      <div v-if="userPaywall" class="flex flex-col items-center mt-6">
        <div class="flex !border !border-surface-3 rounded-xl flex-col w-[320px] !py-4">
          <FormSwitch class="mx-6" label="Linked to Stripe" v-model="props.paywallActive" @change="emit('unlink-stripe')" />
          <hr class="!my-4">
          <dl class="flex justify-between mx-6 text-surface-9">
            <dt class="">Stripe ID</dt>
              <dd class="">{{ userPaywall.externalMasterId }}</dd>
          </dl>
        </div>
        <button
          type="button"
          @click="emit('switch-stripe')"
          class="mt-3 text-sm text-surface-7 hover:text-surface-9 underline underline-offset-2"
        >
          Use a different Stripe account
        </button>
      </div>

      <div class="flex justify-content: flex-start;  mx-auto w-[320px] mt-4">
        <span class="mt-1">
          <icon-base class="text-warning" :width="16" :height="16" fill="none">
            <icon-alert-triangle-solid />
          </icon-base>
        </span>


        <p class="text-sm text-surface-8 mb-4 ml-3">
          Changing your subscription plan will automatically disconnect your Stripe account. Once disconnected, all created paywalls and their associated information will be removed from our system. However, <br>you can still access the transaction details in your Stripe dashboard.
        </p>
      </div>
    </section>
  </main>
</template>

<style scoped>
.custom-list-item {
  @apply before:text-surface-8 before:text-sm before:font-medium before:bg-surface-4 before:rounded-full before:flex before:items-center before:justify-center before:size-8 before:shrink-0 before:mr-4 flex items-start
}
</style>
